import React, { ReactNode } from 'react'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { _tempTheme } from '../../utils/tempTheme'
import { FaPlus, FaCheck } from 'react-icons/fa'
import classnames from 'classnames'

export const Container = styled.div`
	text-align: center;
	border-radius: ${_tempTheme.spacing1};
	font-family: ${({ theme }) => theme.fontFamily};
	max-height: 100%;
	display: flex;
	flex-direction: column;
	h3 {
		margin: 0;
	}
`
interface IQuestionImage {
	$imageProperties?: { width: number; height: number }
	$clickable?: boolean
}

export const ImageStyled = styled.img<IQuestionImage>`
	margin: ${_tempTheme.spacing2} auto;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
	display: flex;
	max-width: 100%;
	max-height: 500px;
	height: auto;
	width: auto;
`
export const ImageWithZoom = styled.img`
	width: 95%;
	height: 95%;
	margin: auto;
	object-fit: contain;
`
export const QuestionWrapper = styled.div`
	padding: 0px;
	.subtitle {
		font-style: normal;
		font-weight: ${_tempTheme.font_normal};
		font-size: ${_tempTheme.text_base};
		line-height: ${({ theme }) => theme.titleFont};
		text-align: center;
		color: ${({ theme }) => theme.white};
		margin: ${_tempTheme.spacing4} auto;
	}
`
export const LabelInformation = styled.div`
	font-family: ${({ theme }) => theme.fontFamily};
	font-size: ${_tempTheme.text_lg};
	color: ${({ theme }) => theme.white};
	background: ${({ theme }) => theme.lightPrimaryColor};
	padding: ${_tempTheme.spacing4};
	border-radius: ${_tempTheme.spacing2};
	margin: ${_tempTheme.spacing2} 0px;
	display: flex;
	justify-content: center;
	text-align: left;
`

interface IChoiceWrapper {
	$withImage: boolean
	$choicesHaveText?: boolean
	$hasQuestionImage?: boolean
	$isPortraitAndOnlyText?: boolean
}

export const ChoiceWrapper = styled.div<IChoiceWrapper>`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: ${_tempTheme.spacing4};
	justify-content: center;
	width: 100%;

	${({ $withImage: withImage }) =>
		withImage &&
		`
		grid-auto-rows: 1fr;
		margin-top: ${_tempTheme.spacing4};
	`}
`

export const InputGroup = styled.div`
	display: flex;
	flex-direction: row;
	&:not(:last-child) {
		margin-bottom: ${_tempTheme.spacing2};
	}
`
interface IIcon {
	$disabled?: boolean
}

export const UploadedImgContainer = styled.div<IIcon>`
	margin: auto ${_tempTheme.spacing2};
	color: ${({ theme }) => theme.primary};
	display: flex;
	pointer-events: ${({ $disabled }) => $disabled && 'none'};
	align-items: center;

	button {
		background: transparent;
		border: none;
		padding: 0;
		height: 18px;
		width: 18px;
	}
	img {
		cursor: pointer;
		height: 18px;
		width: 18px;
		margin: 0;
	}
	input[type='file'] {
		position: absolute !important;
		height: 1px;
		width: 1px;
		overflow: hidden;
		clip: rect(1px, 1px, 1px, 1px);
	}
	label {
		height: 18px;
		width: 18px;
	}

	label.label-focus {
		outline: rgb(0, 95, 204) auto 1px;
	}
`

interface IReadOnlyFormInput {
	$correct?: boolean
}

/**
 * This is the checkbox for a choice connected to a read only question teacher view.
 * It can appear as a circle or a square based on the prop provided, 'shape'
 * @param {'circle' | 'square'} shape - The shape of the marker. circle by default
 * @param {boolean} checkmark - Whether to show a checkmark inside the marker or not
 * @returns
 */
export const ReadOnlyChoiceAnswerMarker = ({
	shape = 'circle',
	checkmark,
}: {
	checkmark?: boolean
	shape?: 'square' | 'circle'
}) => {
	return (
		<div
			className={classnames(
				'size-4 bg-neutral-light pointer-events-none flex items-center box-border justify-center mr-3',
				shape === 'square' ? 'rounded' : 'rounded-full'
			)}>
			{checkmark && (
				<FaCheck aria-label="Correct answer" className="w-3/4 text-success" />
			)}
		</div>
	)
}

export const ReadOnlyFormInput = styled.li<IReadOnlyFormInput>`
	display: flex;
	align-items: center;
	pointer: cursor;
	background: ${({ theme }) => theme.baseLight};

	.question-choice {
		font-size: ${_tempTheme.text_sm};
		color: ${({ theme, $correct }) =>
			$correct ? theme.success : theme.onBase};
	}
`

/**
 * Essentially a radio button that can be selected or not.
 */
export function CheckboxButton({
	selected,
	...props
}: { selected: boolean } & React.ButtonHTMLAttributes<
	HTMLButtonElement
>): ReactNode {
	return (
		<button
			{...props}
			className="border border-brand-horizon-blue rounded-lg flex justify-center items-center mr-2 self-center size-6 p-1">
			{selected && (
				<FaCheck className="text-success" aria-label="Correct Choice" />
			)}
		</button>
	)
}

/**
 * a button with a plus icon on the left, followed by the children. Can be displayed on a light background
 * @param param0
 * @returns
 */
export function AddButton({
	children,
	...props
}: {
	children: ReactNode
} & React.ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
	return (
		<button
			{...props}
			className="text-brand-horizon-blue hover:text-brand-deep-blue active:brightness-75 flex items-center gap-2">
			<FaPlus />
			{children}
		</button>
	)
}

export const StudentAnswer = styled.div`
	.answer .icon {
		display: inline-block;
	}
	.student-response {
		font-style: italic;
		font-size: ${_tempTheme.text_sm};
	}
`

export const QuestionHeader = ({ children }: { children: ReactNode }) => {
	return (
		<div className="font-sm flex items-start font-semibold text-start">
			{children}
		</div>
	)
}

export const ReadOnly = styled.div.attrs({
	className: 'font-brand-headline space-y-2',
})`
	font-weight: normal;
	.invalid-question-cover {
		background-color: black;
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		text-align: center;
		opacity: 0.7;
	}
	.template-wrapper {
		padding: ${_tempTheme.spacing4};
		border: 1px solid ${({ theme }) => theme.boxShadow};
	}

	.invalid-question-text {
		position: absolute;
		color: white;
		top: 25%;
		text-align: center;
		margin: 0 ${_tempTheme.spacing4};
	}

	.student-mark.wrong {
		color: ${({ theme }) => theme.error};
	}
	.student-mark.right {
		color: ${({ theme }) => theme.success};
	}
	.question-phrase {
		font-size: ${_tempTheme.text_base};
		flex: 1;
		padding-left: ${_tempTheme.spacing2};
	}
`

export const QuestionResponseStyled = styled.ul`
	padding: ${_tempTheme.spacing2} 0;

	ul {
		margin: 0;
		padding: 0;
	}
`

interface IQuestionOption {
	$chosen?: boolean
	$correct?: any
	$isReadOnly?: boolean
}
export const QuestionOption = styled.li<IQuestionOption>`
	list-style: none;
	font-family: ${({ theme }) => theme.fontFamily};
	display: flex;
	align-items: center;
	text-align: left;
	padding: 6px 0;
	color: ${({ theme, $chosen, $correct }) =>
		$correct ? theme.success : $chosen ? theme.error : 'inherit'};
	.square {
		background: ${({ theme }) => theme.neutral};
		border: 2px solid ${({ theme }) => theme.neutral};
		width: 12px;
		height: 12px;
		border-radius: 2px;
		margin: auto ${_tempTheme.spacing2} auto 0;
	}

	.selected {
		background: ${({ theme }) => theme.primary};
	}
`

export const Divider = styled.hr`
	background: ${({ theme }) => theme.neutralVariant};
	color: ${({ theme }) => theme.neutralVariant};
	height: 2px;
	border: none;
`
export const IconWrapper = styled.div`
	position: absolute;
	top: -9px;
	z-index: 3;
	right: -${_tempTheme.spacing1};
`
export const QuestionOptionImage = styled.img`
	margin-left: ${_tempTheme.spacing2};
	height: 100px;
	width: auto;
`
export const TitleBold = styled.div`
	font-weight: ${_tempTheme.font_bold};
	margin: ${_tempTheme.spacing2};
	padding-left: ${_tempTheme.spacing2};
`

export const GeneralScoreTitle = styled.div`
	font-family: ${({ theme }) => theme.fontFamily};
	font-weight: ${_tempTheme.font_bold};
	font-style: normal;
	font-weight: ${_tempTheme.font_semibold};
	font-size: ${_tempTheme.text_base};
	line-height: ${_tempTheme.text_2xl};
	text-align: center;
	color: ${({ theme }) => theme.white};
	position: relative;

	.question-index {
		font-family: ${({ theme }) => theme.fontFamily};
		position: absolute;
		font-size: ${_tempTheme.text_2xl};
		line-height: ${_tempTheme.text_2xl};
	}
`

export interface IChoice {
	$correct?: boolean
	$chosen?: boolean
	$isReview?: boolean
	selected?: boolean
	$hasImage?: boolean
	$hasText?: boolean
	$isRow?: boolean
	$isClassResponse?: boolean
}

interface IImageWrapper {
	$hasText?: boolean
	$hasImage?: boolean
	$isQuestionSlot?: boolean
	$isClassResponse?: boolean
}

export const ImageWrapper = styled.div<IImageWrapper>`
	max-width: ${({ $hasText, $isQuestionSlot }) =>
		$hasText && !$isQuestionSlot ? '60px' : '100%'};
	width: ${({ $isClassResponse, $hasImage }) =>
		$isClassResponse && $hasImage ? '100%' : 'auto'};
`

export const OptionContent = styled.div`
	text-align: left;
	font-size: ${_tempTheme.text_lg};
`

export const ImageViewer = styled.img`
	max-width: 300px;
	background: white;
	padding: ${_tempTheme.spacing2};
	border-radius: ${_tempTheme.spacing2};
	box-shadow: 0px ${_tempTheme.spacing1} ${_tempTheme.spacing1} 0px #00000040;
`

interface IUploadImageWrapper {
	$isTextInput?: boolean
}

export const UploadImageWrapper = styled.div<IUploadImageWrapper>`
	margin-right: ${({ $isTextInput }) => $isTextInput && '34px'};
	margin-top: 0.5em;
`
